import { CHTooltip } from '@/components/tooltip';
import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { useMediaControls } from '@rhg/hooks/use-media-controls';
import playerMessages from '@rhg/lang/messages/player';
import {
    Box,
    IconButton,
    Slider,
    SxProps,
    Theme,
    Typography,
} from '@rhg/libs/material';
import {
    Back15,
    ClosedCaptionFilled,
    ClosedCaptionOutline,
    ExpandScreenFilled,
    MinimizeScreenFilled,
    Forward15,
    PauseFilled,
    PlayFilled,
    VolumeOffFilled,
    VolumeOnFilled,
    InfoFilled,
} from '@rhg/libs/material/icons';
import {
    responsivePaddingDefault,
    styleMaxLines,
    sxAppend,
} from '@rhg/libs/material/sx';
import { displayTime } from '@rhg/utils/media';
import { ReactElement, RefObject, useState } from 'react';
import { useIntl } from 'react-intl';
import { MediaSubtitles } from '../../subtitles';
import { PlayerWithTracks } from '../../types';
import { ControlButtonType } from '../types';

const styleVideoControlsOverlay = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
};

const styleBottomContainer = {
    position: 'absolute',
    bottom: -1,
    left: 0,
    width: '100%',
};

const styleVideoControlsContainer = {
    backgroundColor: (theme: Theme) => theme.palette.mask.black['60p'],
    width: '100%',
    py: 4,
    px: {
        sm: DS_SPACING_UNITS.SIDE_SMALL,
        md: DS_SPACING_UNITS.SIDE_MEDIUM,
        lg: DS_SPACING_UNITS.SIDE_LARGE,
    },
};

const styleTimingContainer = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};

const styleTimingSlider = {
    width: '100%',
    mx: 4,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};
const styleTimingControl = {
    width: '100%',
    '& .MuiSlider-thumb:not(.MuiSlider-active)': {
        transition: 'left 0.35s linear',
    },
    '& .MuiSlider-track': {
        transition: 'width 0.35s linear',
    },
};

const styleNonMobileElement = {
    display: { xs: 'none', lg: 'block' },
};

const styleMobileElement = {
    display: { xs: 'block', lg: 'none' },
};

const styleTopAction = {
    pr: 4,
};

const styleTitleSubtitle = { maxWidth: '27%' };

const styleControlsMain = {
    width: '100%',
    display: 'flex',
    justifyContent: {
        xs: 'center',
        sm: 'center',
        md: 'center',
        lg: 'space-between',
    },
    alignItems: 'center',
    position: 'relative',
    minHeight: '48px',
    mb: DS_SPACING_UNITS.CONTENT,
};

const styleControlsCenter = {
    width: '100%',
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};

const styleFullScreenButtonContainer = {
    display: 'flex',
    zIndex: (theme: Theme) => theme.zIndex.modal,
    position: 'absolute',
    top: 0,
    right: 0,
    p: responsivePaddingDefault,
};

const styleFullScreenButton = {
    borderRadius: '50%',
    backgroundColor: (theme: Theme) => theme.palette.mask.black['60p'],
    color: 'action.active',
};

const styleFullScreenButtonFullScreen = {
    color: 'primary.main',
};

const styleIconButton = {
    mx: 4,
    width: 48,
    height: 48,
    '&:hover': {
        backgroundColor: {
            xs: 'mask.black.60p',
            md: 'mask.white.8p',
        },
    },
    '& svg': {
        width: 32,
        height: 32,
    },
};

const stylePlayIconButton = {
    mx: 4,
    width: 48,
    height: 48,
    '& svg': {
        width: 48,
        height: 48,
    },
};

const styleSubtitles = {
    mb: {
        xs: 14 + DS_SPACING_UNITS.CONTENT,
        sm: 14 + DS_SPACING_UNITS.CONTENT,
        md: 14 + DS_SPACING_UNITS.SECTION,
    },
};

export interface VideoControlsProps {
    mediaEl: PlayerWithTracks;
    fullscreenContainerRef: RefObject<Element>;
    isInitiallyMuted?: boolean;
    actionButton?: ReactElement;
    title?: string;
    subtitle?: string;
    sx?: SxProps<Theme>;
    shouldInterruptPlay?: boolean;
    hasAutoPlayRestriction?: boolean;
    onControlClick?: (buttonType: ControlButtonType) => void;
}
export function VideoControls({
    mediaEl,
    isInitiallyMuted,
    fullscreenContainerRef,
    actionButton,
    title,
    subtitle,
    sx,
    shouldInterruptPlay,
    onControlClick,
    hasAutoPlayRestriction,
}: VideoControlsProps) {
    const { formatMessage } = useIntl();
    const [isTooltipOpen, setIsTooltipOpen] = useState(true);

    const {
        isFullscreen,
        isMuted,
        isShowingSubtitles,
        isPaused,
        currentCue,
        timingState,
        togglePlay,
        toggleSubtitles,
        toggleFullscreen,
        toggleMuted,
        setTime,
    } = useMediaControls({
        type: 'video',
        mediaEl,
        fullscreenContainerRef,
        isInitiallyMuted,
        shouldInterruptPlay,
    });

    const handleTogglePlay = () => {
        if (isPaused && hasAutoPlayRestriction) {
            setIsTooltipOpen(false);
        }
        if (onControlClick) {
            // The reverse of the current state, prior to calling togglePlay
            onControlClick(isPaused ? 'play' : 'pause');
        }

        togglePlay();
    };

    return (
        <Box sx={sxAppend(styleVideoControlsOverlay, sx)}>
            <Box sx={styleFullScreenButtonContainer}>
                <Box sx={sxAppend(styleMobileElement, styleTopAction)}>
                    {actionButton}
                </Box>
                <IconButton
                    sx={sxAppend(
                        styleFullScreenButton,
                        styleFullScreenButtonFullScreen
                    )}
                    size="large"
                    trackButton="VideoPlayerToggleFullscreen"
                    aria-label={formatMessage(
                        playerMessages['player.toggle_fullscreen']
                    )}
                    onClick={() => toggleFullscreen()}
                >
                    {isFullscreen ? (
                        <MinimizeScreenFilled />
                    ) : (
                        <ExpandScreenFilled />
                    )}
                </IconButton>
            </Box>
            <Box sx={styleBottomContainer}>
                {isShowingSubtitles && (
                    <MediaSubtitles
                        cue={currentCue}
                        isMuted={isMuted}
                        sx={styleSubtitles}
                    />
                )}
                <Box sx={styleVideoControlsContainer}>
                    <Box sx={styleControlsMain}>
                        <Box
                            sx={sxAppend(
                                styleNonMobileElement,
                                styleTitleSubtitle
                            )}
                        >
                            <Typography
                                variant="h6"
                                component="p"
                                color="text.body"
                                sx={styleMaxLines(2)}
                            >
                                {title}
                            </Typography>
                            <Typography
                                variant="subtitle2"
                                component="p"
                                color="text.sub"
                                sx={styleMaxLines(2)}
                            >
                                {subtitle}
                            </Typography>
                        </Box>
                        <Box sx={styleControlsCenter}>
                            <IconButton
                                trackButton={
                                    isShowingSubtitles
                                        ? 'VideoPlayerDisableCC'
                                        : 'VideoPlayerEnableCC'
                                }
                                onClick={() => toggleSubtitles()}
                                aria-label={
                                    isShowingSubtitles
                                        ? formatMessage(
                                              playerMessages[
                                                  'player.disable_cc'
                                              ]
                                          )
                                        : formatMessage(
                                              playerMessages['player.enable_cc']
                                          )
                                }
                                sx={styleIconButton}
                            >
                                {isShowingSubtitles ? (
                                    <ClosedCaptionFilled color="info" />
                                ) : (
                                    <ClosedCaptionOutline color="inherit" />
                                )}
                            </IconButton>
                            <IconButton
                                trackButton="VideoPlayerBack15"
                                aria-label={formatMessage(
                                    playerMessages['player.backward_15']
                                )}
                                onClick={() =>
                                    setTime(
                                        Math.max(
                                            timingState.currentTime - 15,
                                            0
                                        )
                                    )
                                }
                                sx={styleIconButton}
                            >
                                <Back15 />
                            </IconButton>
                            {hasAutoPlayRestriction ? (
                                <CHTooltip
                                    isOpen={isTooltipOpen}
                                    startIcon={<InfoFilled />}
                                    topLabel={formatMessage(
                                        playerMessages[
                                            'player.autoplay_error.top_label'
                                        ]
                                    )}
                                    content={formatMessage(
                                        playerMessages[
                                            'player.autoplay_error.content'
                                        ]
                                    )}
                                >
                                    <IconButton
                                        trackButton={
                                            timingState.isPaused
                                                ? 'VideoPlayerPlay'
                                                : 'VideoPlayerPause'
                                        }
                                        onClick={handleTogglePlay}
                                        aria-label={
                                            timingState.isPaused
                                                ? formatMessage(
                                                      playerMessages[
                                                          'player.play'
                                                      ]
                                                  )
                                                : formatMessage(
                                                      playerMessages[
                                                          'player.pause'
                                                      ]
                                                  )
                                        }
                                        sx={stylePlayIconButton}
                                    >
                                        {timingState.isPaused ? (
                                            <PlayFilled />
                                        ) : (
                                            <PauseFilled />
                                        )}
                                    </IconButton>
                                </CHTooltip>
                            ) : (
                                <IconButton
                                    trackButton={
                                        timingState.isPaused
                                            ? 'VideoPlayerPlay'
                                            : 'VideoPlayerPause'
                                    }
                                    onClick={handleTogglePlay}
                                    aria-label={
                                        timingState.isPaused
                                            ? formatMessage(
                                                  playerMessages['player.play']
                                              )
                                            : formatMessage(
                                                  playerMessages['player.pause']
                                              )
                                    }
                                    sx={stylePlayIconButton}
                                >
                                    {timingState.isPaused ? (
                                        <PlayFilled />
                                    ) : (
                                        <PauseFilled />
                                    )}
                                </IconButton>
                            )}
                            <IconButton
                                trackButton="VideoPlayerForward15"
                                aria-label={formatMessage(
                                    playerMessages['player.forward_15']
                                )}
                                onClick={() =>
                                    setTime(
                                        Math.min(
                                            timingState.currentTime + 15,
                                            timingState.duration
                                        )
                                    )
                                }
                                sx={styleIconButton}
                            >
                                <Forward15 />
                            </IconButton>
                            <IconButton
                                trackButton={
                                    isMuted
                                        ? 'VideoPlayerUnmute'
                                        : 'VideoPlayerMute'
                                }
                                onClick={() => toggleMuted()}
                                sx={styleIconButton}
                                aria-label={
                                    isMuted
                                        ? formatMessage(
                                              playerMessages['player.unmute']
                                          )
                                        : formatMessage(
                                              playerMessages['player.mute']
                                          )
                                }
                            >
                                {isMuted ? (
                                    <VolumeOffFilled />
                                ) : (
                                    <VolumeOnFilled />
                                )}
                            </IconButton>
                        </Box>
                        <Box sx={styleNonMobileElement}>{actionButton}</Box>
                    </Box>
                    <Box sx={styleTimingContainer}>
                        <Typography
                            variant="subtitle2"
                            color="text.body"
                            component="div"
                            aria-label={formatMessage(
                                playerMessages['player.current_time'],
                                {
                                    time: displayTime(timingState.currentTime),
                                }
                            )}
                        >
                            {displayTime(timingState.currentTime)}
                        </Typography>
                        <Box sx={styleTimingSlider}>
                            <Slider
                                sx={styleTimingControl}
                                value={timingState.currentTime}
                                max={timingState.duration}
                                aria-label={formatMessage(
                                    playerMessages['player.seek_slider']
                                )}
                                getAriaValueText={(sliderValue) =>
                                    formatMessage(
                                        playerMessages['player.current_time'],
                                        {
                                            time: displayTime(sliderValue),
                                        }
                                    )
                                }
                                step={timingState.duration / 1000}
                                size="small"
                                onChangeCommitted={(_, newTime) => {
                                    setTime(newTime as number);
                                }}
                            />
                        </Box>
                        <Typography
                            variant="subtitle2"
                            color="text.body"
                            component="div"
                            aria-label={formatMessage(
                                playerMessages['player.duration'],
                                {
                                    time: displayTime(timingState.duration),
                                }
                            )}
                        >
                            {displayTime(timingState.duration)}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
